import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Progress, Space, Tag, Typography, Spin, theme,
} from 'antd';
import {
  CalculatorOutlined, ReloadOutlined, LinkOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { conciliacionIvaService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';
import { construirCsvConciliacion, nombreArchivoConciliacion } from '../../utils/conciliacionIvaCsv';

const { Text, Paragraph } = Typography;

const pesos = (v) =>
  `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Conciliación de IVA: por mes, trasladado, acreditable, saldo del mes y el
 * arrastre —con cuánto saldo a favor entró, cuánto acreditó, cuánto queda por
 * pagar y con cuánto cerró—.
 *
 * Reemplaza la hoja de cálculo que el gestor lleva a mano.
 *
 * TRES COSAS QUE LA PANTALLA NO PUEDE DISIMULAR:
 *
 *  - Un mes SIN CALCULAR no es un mes en cero. Se pinta distinto, porque
 *    confundirlos es lo que produce un saldo a favor inventado.
 *  - Si la cadena está ROTA, se dice dónde. Un acumulado que salta un mes con IVA
 *    a cargo queda por encima del real, y eso sugiere no pagar cuando sí hay que
 *    pagar.
 *  - Cada mes muestra CUÁNDO se calculó. Un agregado de CFDI envejece solo:
 *    llegan comprobantes tarde y se cancelan otros.
 *
 * TODA la cadena sale de nuestro cálculo sobre CFDI. Lo declarado en el acuse se
 * muestra al lado como contraste y no entra a ningún importe: la funcionalidad
 * existe para estimar el IVA correcto SIN haber declarado.
 */
const ConciliacionIvaPanel = ({ empresa }) => {
  const { token } = theme.useToken();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const [corriendo, setCorriendo] = useState(false);
  const [detener, setDetener] = useState(false);
  const detenerRef = useRef(false);
  const [hechos, setHechos] = useState(0);
  const [porHacer, setPorHacer] = useState(0);
  const [enCurso, setEnCurso] = useState(null);
  const [fallos, setFallos] = useState([]);
  const [termino, setTermino] = useState(false);
  const [encadenando, setEncadenando] = useState(false);

  useEffect(() => {
    detenerRef.current = detener;
  }, [detener]);

  const cargar = useCallback(async () => {
    if (!empresa?.id) return;
    setCargando(true);
    setError('');

    try {
      setDatos(await conciliacionIvaService.getConciliacion(empresa.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo consultar la conciliación');
    } finally {
      setCargando(false);
    }
  }, [empresa?.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const disponible = datos?.disponible;
  const meses = datos?.meses || [];
  const pendientes = meses.filter((m) => !m.calculado_en);
  const cadena = datos?.cadena;

  const calcular = async () => {
    const cola = pendientes;
    if (cola.length === 0) return;

    setCorriendo(true);
    setDetener(false);
    detenerRef.current = false;
    setTermino(false);
    setHechos(0);
    setPorHacer(cola.length);
    setFallos([]);

    const errores = [];

    for (let i = 0; i < cola.length; i++) {
      if (detenerRef.current) break;

      const mes = cola[i];
      setEnCurso(mes.period);

      try {
        // eslint-disable-next-line no-await-in-loop
        const salida = await conciliacionIvaService.calcularPeriodo(empresa.id, mes.period);
        if (!salida?.ok) {
          errores.push({ period: mes.period, motivo: salida?.motivo || 'No se pudo calcular' });
        }
      } catch (err) {
        const cuerpo = err?.response?.data;
        errores.push({
          period: mes.period,
          motivo: cuerpo?.motivo || cuerpo?.error || 'No se pudo calcular',
        });
      }

      setFallos([...errores]);
      setHechos(i + 1);
    }

    setEnCurso(null);
    setCorriendo(false);
    setTermino(true);
    await cargar();
  };

  // Reescribir la cadena sin recalcular ningún mes. Hace falta para el
  // contribuyente que ya tiene sus 24 meses calculados: no hay ningún mes que
  // postear, así que su acumulado nunca llegaría a persistirse.
  const reencadenar = async () => {
    setEncadenando(true);
    setError('');

    try {
      await conciliacionIvaService.reencadenar(empresa.id);
      await cargar();
    } catch (err) {
      const cuerpo = err?.response?.data;
      setError(cuerpo?.motivo || cuerpo?.error || 'No se pudo guardar el acumulado');
    } finally {
      setEncadenando(false);
    }
  };

  /**
   * Descarga la tabla tal como está en pantalla.
   *
   * Se arma con los datos que ya están aquí, no pidiendo un CSV al servidor: el
   * archivo se descarga para respaldar una cifra, así que no puede decir algo
   * distinto de lo que se está viendo.
   *
   * NO se bloquea con la cadena rota. Exportar una tabla a medias es legítimo —el
   * archivo conserva los vacíos y la columna de estado— y bloquearlo obligaría a
   * copiar a mano justo cuando hace falta revisar qué falta.
   */
  const descargarCsv = () => {
    const texto = construirCsvConciliacion({ meses, cadena });
    const url = window.URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));

    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivoConciliacion(datos?.tax_id || empresa?.tax_id);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // El arrastre de cada mes sale de la cadena. Un mes SIN fila de cadena no es un
  // mes en cero: o no está calculado, o está después de la ruptura. En los dos
  // casos se pinta «—», nunca «$0.00», que se leería como un hecho.
  const arrastreDe = (period) =>
    (cadena?.desglose || []).find((d) => d.period === period) || null;

  const importeOGuion = (valor) => (
    valor === null || valor === undefined
      ? <Text type="secondary">—</Text>
      : pesos(valor)
  );

  const columnas = [
    { title: 'Mes', dataIndex: 'period', key: 'period' },
    {
      title: 'IVA trasladado',
      dataIndex: 'iva_trasladado',
      key: 'trasladado',
      render: (v, fila) => (fila.calculado_en ? pesos(v) : <Text type="secondary">—</Text>),
    },
    {
      title: 'IVA acreditable',
      dataIndex: 'iva_acreditable',
      key: 'acreditable',
      render: (v, fila) => (fila.calculado_en ? pesos(v) : <Text type="secondary">—</Text>),
    },
    {
      title: 'Saldo del mes',
      dataIndex: 'saldo',
      key: 'saldo',
      render: (v, fila) => {
        if (!fila.calculado_en) return <Text type="secondary">—</Text>;
        const n = Number(v || 0);
        return <Text style={n < 0 ? { color: token.colorSuccess } : undefined}>{pesos(n)}</Text>;
      },
    },
    {
      title: 'Saldo a favor previo',
      key: 'previo',
      render: (_, fila) => importeOGuion(arrastreDe(fila.period)?.disponible_antes),
    },
    {
      title: 'Acreditado',
      key: 'acreditado',
      render: (_, fila) => importeOGuion(arrastreDe(fila.period)?.acreditado),
    },
    {
      title: 'IVA por pagar',
      key: 'a_pagar',
      render: (_, fila) => {
        const c = arrastreDe(fila.period);
        if (!c) return <Text type="secondary">—</Text>;
        const n = Number(c.a_pagar || 0);
        return <Text style={n > 0 ? { color: token.colorError } : undefined}>{pesos(n)}</Text>;
      },
    },
    {
      title: 'Saldo a favor al cierre',
      key: 'al_cierre',
      render: (_, fila) => {
        const c = arrastreDe(fila.period);
        if (!c) return <Text type="secondary">—</Text>;
        const n = Number(c.saldo_despues || 0);
        return <Text style={n > 0 ? { color: token.colorSuccess } : undefined}>{pesos(n)}</Text>;
      },
    },
    {
      title: 'Declarado (informativo)',
      key: 'declarado',
      render: (_, fila) => {
        // Contraste, no base: no entra a ningún importe de esta tabla.
        if (!fila.declarado) return <Text type="secondary">—</Text>;
        const esFavor = fila.declarado.signo === 'favor';
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {pesos(fila.declarado.importe)} {esFavor ? 'a favor' : 'a cargo'}
          </Text>
        );
      },
    },
    {
      title: 'Estado',
      key: 'origen',
      render: (_, fila) => (fila.calculado_en
        ? <Tag color="blue">Calculado</Tag>
        : <Tag color="orange">Sin calcular</Tag>),
    },
    {
      title: 'Calculado',
      dataIndex: 'calculado_en',
      key: 'calculado_en',
      render: (v) => (v
        ? <Text type="secondary" style={{ fontSize: 12 }}>{String(v).slice(0, 16)}</Text>
        : <Text type="secondary" style={{ fontSize: 12 }}>Faltan documentos</Text>),
    },
  ];

  if (cargando && !datos) {
    return <div><Spin /> <Text type="secondary">Consultando la conciliación…</Text></div>;
  }

  const desconocidos = cadena?.meses_desconocidos || [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {!!error && <Alert type="error" showIcon message={error} />}

      {/* El histórico incompleto se advierte aquí: calcular sobre él no daría una
          conciliación incompleta, daría una equivocada. */}
      {disponible && !disponible.puede && (
        <Alert
          type="warning"
          showIcon
          message="Todavía no se puede conciliar"
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Text>{disponible.motivo}</Text>
              {(disponible.ventanas_faltantes || []).length > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Falta: {disponible.ventanas_faltantes.join(' · ')}. Solicítalo en «Histórico para
                  conciliación», aquí arriba.
                </Text>
              )}
            </Space>
          }
        />
      )}

      {/* La cadena rota es lo más importante de esta pantalla: sin este aviso, el
          acumulado que se ve parece cubrir todo el rango cuando no lo hace. */}
      {!!cadena?.rota_en && (
        <Alert
          type="warning"
          showIcon
          message={`El acumulado se corta en ${cadena.rota_en}`}
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Text>
                Ese mes no está calculado, así que desde ahí no se puede saber cuánto saldo a favor
                queda. Los meses posteriores no muestran arrastre.
              </Text>
              {desconocidos.length > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Sin calcular: {desconocidos.join(' · ')}
                </Text>
              )}
            </Space>
          }
        />
      )}

      {!!cadena && cadena.persistida === false && (
        <Alert
          type="info"
          showIcon
          message="El acumulado no está guardado"
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text>
                Se está calculando al consultar. Guárdalo para que la estimación del mes pueda
                partir de él.
              </Text>
              <Button
                size="small"
                icon={<LinkOutlined />}
                loading={encadenando}
                onClick={reencadenar}
              >
                Guardar el acumulado
              </Button>
            </Space>
          }
        />
      )}

      <ResponsiveTable
        rowKey="period"
        loading={cargando}
        dataSource={meses}
        columns={columnas}
        pagination={false}
        size="small"
        scroll={{ x: true }}
      />

      {corriendo && (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Progress percent={porHacer ? Math.round((hechos / porHacer) * 100) : 0} />
          <Text>{hechos} / {porHacer} meses</Text>
          {enCurso && <Text type="secondary">Calculando {enCurso}…</Text>}
        </Space>
      )}

      {termino && (
        <Alert
          type={fallos.length > 0 ? 'warning' : 'success'}
          showIcon
          message={fallos.length > 0 ? 'Algunos meses no se pudieron calcular' : 'Conciliación actualizada'}
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {fallos.map((f) => (
                <Text key={f.period} type="secondary" style={{ fontSize: 12 }}>
                  {f.period}: {f.motivo}
                </Text>
              ))}
            </Space>
          }
        />
      )}

      <Space wrap>
        <Button
          type="primary"
          icon={<CalculatorOutlined />}
          loading={corriendo}
          disabled={corriendo || !disponible?.puede || pendientes.length === 0}
          onClick={calcular}
        >
          {pendientes.length > 0
            ? `Calcular ${pendientes.length} mes${pendientes.length === 1 ? '' : 'es'}`
            : 'Nada que calcular'}
        </Button>

        {corriendo && <Button danger onClick={() => setDetener(true)}>Detener</Button>}

        {!corriendo && (
          <Button icon={<ReloadOutlined />} disabled={cargando} onClick={cargar}>
            Actualizar
          </Button>
        )}

        {!corriendo && (
          <Button
            icon={<DownloadOutlined />}
            disabled={cargando || meses.length === 0}
            onClick={descargarCsv}
          >
            Descargar CSV
          </Button>
        )}
      </Space>

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        Estimación informativa derivada de los CFDI: no sustituye la declaración y no contempla
        devoluciones ni compensaciones. Supone que se acreditó todo lo posible cada mes. Un mes sin
        calcular no es un mes en cero, es un mes del que faltan documentos. Lo declarado en el acuse
        se muestra solo para comparar y no entra al cálculo.
      </Paragraph>
    </Space>
  );
};

export default ConciliacionIvaPanel;
